/**
 * Job Queue Visualization Component
 *
 * Visual representation of job queue metrics and worker utilization
 *
 * Features:
 * - Queue depth chart
 * - Processing rate visualization
 * - Worker utilization display
 * - Job type distribution
 * - Real-time metrics
 */

import React from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Grid,
  Paper,
  Progress,
  RingProgress,
  ThemeIcon,
  Divider,
  Alert,
  Box
} from '@mantine/core';
import {
  IconClock,
  IconBolt,
  IconTrendingUp,
  IconUsers,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconReload,
  IconChartBar
} from '@tabler/icons-react';
import type { QueueMetrics } from '../hooks/useJobMonitoring';

interface JobQueueVisualizationProps {
  metrics: QueueMetrics;
}

export function JobQueueVisualization({ metrics }: JobQueueVisualizationProps) {
  /**
   * Get health status color
   */
  const getHealthColor = (): string => {
    if (metrics.errorRate > 0.2) return 'red';
    if (metrics.errorRate > 0.1) return 'orange';
    if (metrics.queueDepth > 100) return 'yellow';
    return 'green';
  };

  /**
   * Get utilization color
   */
  const getUtilizationColor = (utilization: number): string => {
    if (utilization > 0.9) return 'orange';
    if (utilization > 0.7) return 'blue';
    if (utilization > 0.5) return 'cyan';
    return 'green';
  };

  /**
   * Format duration
   */
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  /**
   * Get top job types
   */
  const topJobTypes = Object.entries(metrics.jobsByType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const totalJobs = Object.values(metrics.jobsByType).reduce((sum, count) => sum + count, 0);

  return (
    <Stack gap="lg">
      {/* Health Status Alert */}
      <Alert
        icon={<IconCheck size={16} />}
        color={getHealthColor()}
        title="Queue Health Status"
      >
        <Text size="sm">
          {metrics.errorRate > 0.2 ? 'Critical: High error rate detected' :
           metrics.errorRate > 0.1 ? 'Warning: Elevated error rate' :
           metrics.queueDepth > 100 ? 'Warning: Queue depth is high' :
           'Healthy: All systems operating normally'}
        </Text>
        <Text size="xs" mt="xs" c="dimmed">
          Error rate: {(metrics.errorRate * 100).toFixed(1)}% | Queue depth: {metrics.queueDepth} |
          Workers: {metrics.activeWorkers}/{metrics.totalWorkers}
        </Text>
      </Alert>

      {/* Key Metrics Grid */}
      <Grid>
        {/* Queue Depth */}
        <Grid.Col span={3}>
          <Card withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <ThemeIcon color="blue" variant="light">
                  <IconChartBar size={20} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Queue Depth</Text>
                  <Text size="xl" fw={700}>{metrics.queueDepth}</Text>
                </div>
              </Group>
              <Progress
                value={Math.min((metrics.queueDepth / 100) * 100, 100)}
                color={metrics.queueDepth > 100 ? 'red' : metrics.queueDepth > 50 ? 'yellow' : 'blue'}
                size="sm"
              />
              <Text size="xs" c="dimmed">
                {metrics.queueDepth > 100 ? 'High load' :
                 metrics.queueDepth > 50 ? 'Moderate load' :
                 'Normal load'}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Processing Rate */}
        <Grid.Col span={3}>
          <Card withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <ThemeIcon color="green" variant="light">
                  <IconBolt size={20} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Processing Rate</Text>
                  <Text size="xl" fw={700}>{metrics.processingRate.toFixed(1)}/s</Text>
                </div>
              </Group>
              <Progress
                value={Math.min((metrics.processingRate / 10) * 100, 100)}
                color="green"
                size="sm"
              />
              <Text size="xs" c="dimmed">
                {metrics.throughputPerHour} jobs/hour
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Avg Wait Time */}
        <Grid.Col span={3}>
          <Card withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <ThemeIcon color="orange" variant="light">
                  <IconClock size={20} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Avg Wait Time</Text>
                  <Text size="xl" fw={700}>{formatDuration(metrics.avgWaitTimeMs)}</Text>
                </div>
              </Group>
              <Progress
                value={Math.min((metrics.avgWaitTimeMs / 10000) * 100, 100)}
                color={metrics.avgWaitTimeMs > 5000 ? 'red' : metrics.avgWaitTimeMs > 2000 ? 'yellow' : 'green'}
                size="sm"
              />
              <Text size="xs" c="dimmed">
                Avg processing: {formatDuration(metrics.avgProcessingTimeMs)}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Worker Utilization */}
        <Grid.Col span={3}>
          <Card withBorder>
            <Stack gap="xs" ta="center">
              <Text size="xs" c="dimmed" fw={600}>Worker Utilization</Text>
              <RingProgress
                size={100}
                thickness={10}
                sections={[
                  { value: metrics.workerUtilization * 100, color: getUtilizationColor(metrics.workerUtilization) }
                ]}
                label={
                  <Text ta="center">
                    <Text fw={700} size="lg">{Math.round(metrics.workerUtilization * 100)}%</Text>
                  </Text>
                }
              />
              <Text size="xs" c="dimmed">
                {metrics.activeWorkers} / {metrics.totalWorkers} active
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Job Status Distribution */}
      <Card withBorder>
        <Text fw={600} mb="md">Job Status Distribution</Text>
        <Grid>
          <Grid.Col span={2.4}>
            <Paper p="md" withBorder bg="blue.0">
              <Stack gap="xs" ta="center">
                <ThemeIcon color="blue" variant="light" size="lg">
                  <IconClock size={20} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">Queued</Text>
                <Text size="xl" fw={700}>{metrics.jobsByStatus.queued}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2.4}>
            <Paper p="md" withBorder bg="cyan.0">
              <Stack gap="xs" ta="center">
                <ThemeIcon color="cyan" variant="light" size="lg">
                  <IconPlayerPlay size={20} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">Running</Text>
                <Text size="xl" fw={700}>{metrics.jobsByStatus.running}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2.4}>
            <Paper p="md" withBorder bg="green.0">
              <Stack gap="xs" ta="center">
                <ThemeIcon color="green" variant="light" size="lg">
                  <IconCheck size={20} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">Completed</Text>
                <Text size="xl" fw={700}>{metrics.jobsByStatus.completed}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2.4}>
            <Paper p="md" withBorder bg="red.0">
              <Stack gap="xs" ta="center">
                <ThemeIcon color="red" variant="light" size="lg">
                  <IconX size={20} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">Failed</Text>
                <Text size="xl" fw={700}>{metrics.jobsByStatus.failed}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2.4}>
            <Paper p="md" withBorder bg="yellow.0">
              <Stack gap="xs" ta="center">
                <ThemeIcon color="yellow" variant="light" size="lg">
                  <IconReload size={20} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">Retrying</Text>
                <Text size="xl" fw={700}>{metrics.jobsByStatus.retrying}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Job Type Distribution */}
      <Card withBorder>
        <Text fw={600} mb="md">Top Job Types</Text>
        <Stack gap="sm">
          {topJobTypes.map(([jobType, count]) => {
            const percentage = totalJobs > 0 ? (count / totalJobs) * 100 : 0;
            return (
              <div key={jobType}>
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <Badge size="sm" variant="light">
                      {jobType.replace(/_/g, ' ')}
                    </Badge>
                    <Text size="sm" c="dimmed">{count} jobs</Text>
                  </Group>
                  <Text size="sm" fw={600}>{percentage.toFixed(1)}%</Text>
                </Group>
                <Progress value={percentage} size="md" />
              </div>
            );
          })}
          {topJobTypes.length === 0 && (
            <Text c="dimmed" ta="center" py="md">
              No job data available
            </Text>
          )}
        </Stack>
      </Card>

      {/* Performance Indicators */}
      <Grid>
        <Grid.Col span={6}>
          <Card withBorder>
            <Text fw={600} mb="md">Queue Performance</Text>
            <Stack gap="md">
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Throughput</Text>
                  <Text size="sm" fw={600}>{metrics.throughputPerHour} jobs/hour</Text>
                </Group>
                <Progress
                  value={Math.min((metrics.throughputPerHour / 1000) * 100, 100)}
                  color="blue"
                  size="sm"
                />
              </div>
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Error Rate</Text>
                  <Text size="sm" fw={600} c={metrics.errorRate > 0.1 ? 'red' : 'green'}>
                    {(metrics.errorRate * 100).toFixed(2)}%
                  </Text>
                </Group>
                <Progress
                  value={metrics.errorRate * 100}
                  color={metrics.errorRate > 0.1 ? 'red' : 'green'}
                  size="sm"
                />
              </div>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={6}>
          <Card withBorder>
            <Text fw={600} mb="md">Worker Status</Text>
            <Stack gap="md">
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Active Workers</Text>
                  <Text size="sm" fw={600}>{metrics.activeWorkers} / {metrics.totalWorkers}</Text>
                </Group>
                <Progress
                  value={(metrics.activeWorkers / metrics.totalWorkers) * 100}
                  color="cyan"
                  size="sm"
                />
              </div>
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">Utilization</Text>
                  <Text size="sm" fw={600}>{Math.round(metrics.workerUtilization * 100)}%</Text>
                </Group>
                <Progress
                  value={metrics.workerUtilization * 100}
                  color={getUtilizationColor(metrics.workerUtilization)}
                  size="sm"
                />
              </div>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Recommendations */}
      {(metrics.queueDepth > 100 || metrics.errorRate > 0.1 || metrics.workerUtilization > 0.9) && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Performance Recommendations">
          <Stack gap="xs">
            {metrics.queueDepth > 100 && (
              <Text size="sm">• Consider scaling up workers to handle high queue depth</Text>
            )}
            {metrics.errorRate > 0.1 && (
              <Text size="sm">• Investigate recurring errors - error rate is elevated</Text>
            )}
            {metrics.workerUtilization > 0.9 && (
              <Text size="sm">• Worker utilization is very high - consider adding more workers</Text>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
